// ============================================================
// Website Configuration Service & Normalizer
// ============================================================
// Takes raw Public API response and merges it with mock defaults.
// Ensures that real backend data (school, settings, website, domain) is rendered on the UI,
// while any missing sections or fields gracefully fallback to mock data.

import { mockSchoolWebsiteData } from '@/mock/mockSchoolWebsiteData';
import { publicSchoolApi } from '@/api/publicSchool.api';
import config from '@/config';

/**
 * Safely extracts a non-empty value or falls back to default.
 */
function getValue(apiVal, defaultVal) {
  if (apiVal === null || apiVal === undefined || apiVal === '') return defaultVal;
  if (Array.isArray(apiVal) && apiVal.length === 0) return defaultVal;
  return apiVal;
}

/**
 * Formats image URLs from backend relative paths to full accessible URLs.
 */
function formatImageUrl(path) {
  if (!path || typeof path !== 'string' || path.trim().length === 0) return null;
  const trimmed = path.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return trimmed;
  }
  const apiBase = (config.api?.baseURL || '').replace(/\/api\/v1\/?$/, '').replace(/\/api\/?$/, '');
  const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${apiBase}${cleanPath}`;
}

/**
 * Normalizes raw API response object and merges missing fields with mock defaults.
 */
export function normalizeAndMergeWebsiteConfig(apiData, fallbackMock = mockSchoolWebsiteData) {
  if (!apiData || typeof apiData !== 'object') {
    return { ...fallbackMock };
  }

  const raw = apiData.data || apiData;

  if (raw.detail || raw.error || (raw.success === false && !raw.school && !raw.settings && !raw.website)) {
    return { ...fallbackMock };
  }

  const school = raw.school || {};
  const settings = raw.settings || {};
  const website = raw.website || {};
  const domain = raw.domain || {};

  let contentObj = {};
  if (website.content) {
    if (typeof website.content === 'string') {
      try { contentObj = JSON.parse(website.content); } catch (e) {}
    } else {
      contentObj = website.content;
    }
  }

  const schoolName = school.name || website.school_name || raw.school_name || raw.name || null;
  const tagline = website.tagline || website.hero_subtitle || raw.tagline || contentObj?.tagline || contentObj?.hero?.subtitle || null;
  const logo = formatImageUrl(
    settings.logo_url ||
    settings.logo ||
    website.logo ||
    raw.logo_url ||
    raw.logo ||
    (school.logo && school.logo !== 'logo.png' ? school.logo : null) ||
    raw.primary_logo
  ) || null;

  const favicon = formatImageUrl(
    settings.favicon_url ||
    settings.favicon ||
    website.favicon ||
    raw.favicon_url ||
    (school.favicon && school.favicon !== 'favicon.ico' ? school.favicon : null) ||
    raw.favicon
  ) || null;
  const loginImage = formatImageUrl(
    settings.login_image_url ||
    settings.login_image ||
    website.login_image ||
    raw.login_image_url ||
    raw.login_image ||
    contentObj?.login_image ||
    contentObj?.hero?.image
  ) || null;
  
  const primaryColor = settings.primary_color || raw.primary_color || null;
  const secondaryColor = settings.secondary_color || raw.secondary_color || null;
  const template = website.template || raw.template || 'default';
  
  const websiteEnabled = settings.website_enabled !== undefined ? Boolean(settings.website_enabled) : (raw.website_enabled !== undefined ? Boolean(raw.website_enabled) : true);
  const erpEnabled = settings.erp_enabled !== undefined ? Boolean(settings.erp_enabled) : (raw.erp_enabled !== undefined ? Boolean(raw.erp_enabled) : true);

  const addressComponents = [
    website.address || school.address || raw.address,
    school.city,
    school.state,
    school.postal_code || school.postalCode,
    school.country,
  ].filter(Boolean);

  return {
    school_name: schoolName,
    tagline,
    logo,
    favicon,
    primary_color: primaryColor,
    secondary_color: secondaryColor,
    login_image: loginImage,
    template,
    website_enabled: websiteEnabled,
    erp_enabled: erpEnabled,

    // Extracted directly from contentObj
    hero_title: contentObj?.hero?.title || null,
    hero_subtitle: contentObj?.hero?.subtitle || null,
    hero_image: formatImageUrl(contentObj?.hero?.image) || null,
    hero_cta_primary: contentObj?.hero?.cta_primary || null,
    hero_cta_secondary: contentObj?.hero?.cta_secondary || null,

    quick_info: contentObj?.quick_info || null,
    
    about_title: contentObj?.about?.title || null,
    about_content: contentObj?.about?.content || null,
    about_image: formatImageUrl(contentObj?.about?.image) || null,
    
    principal_name: contentObj?.principal?.name || null,
    principal_title: contentObj?.principal?.title || null,
    principal_message: contentObj?.principal?.message || null,
    principal_image: formatImageUrl(contentObj?.principal?.image) || null,
    
    school_highlights: contentObj?.school_highlights || [],
    academics: contentObj?.academics || [],
    facilities: contentObj?.facilities || [],
    why_choose_us: contentObj?.why_choose_us || [],
    statistics: contentObj?.statistics || [],
    gallery: contentObj?.gallery || [],
    video_gallery: contentObj?.video_gallery || [],
    events: contentObj?.events || [],
    news: contentObj?.news || [],
    testimonials: contentObj?.testimonials || [],
    faculty: contentObj?.faculty || [],
    admission_process: contentObj?.admission_process || [],
    
    cta: contentObj?.cta || null,
    
    contact: {
      email: contentObj?.contact?.email || website.contact_email || school.email || null,
      phone: contentObj?.contact?.phone || website.contact_phone || school.phone || null,
      address: contentObj?.contact?.address || (addressComponents.length > 0 ? addressComponents.join(', ') : null),
      google_map_embed: contentObj?.contact?.google_map_embed || null,
    },
    
    social_media: contentObj?.social_media || {
      facebook: null, twitter: null, instagram: null, youtube: null
    },
  };
}

export const websiteConfigService = {
  fetchResolvedWebsiteConfig: async () => {
    try {
      const apiData = await publicSchoolApi.getPublicSchoolConfig();
      return normalizeAndMergeWebsiteConfig(apiData, mockSchoolWebsiteData);
    } catch (err) {
      console.warn('Public School API fetch returned fallback mock data:', err.message || err);
      return normalizeAndMergeWebsiteConfig(null, mockSchoolWebsiteData);
    }
  },
};
